import React, {useCallback, useEffect, useId, useRef, useState} from 'react';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {isNetworkError, isOk} from '../../utils/apiResponse';
import {formatShortDate} from '../../utils/localizedDates';
import {Spinner} from '../Spinner';
import {copyText} from '../agency/prompts/copyText';
import {createConnectionLink, listConnectionLinks, revokeConnectionLink} from './connectionLinksApi';
import {AgencyClientPicker, asClientId, useAgencyClients} from './AgencyClientPicker';
import './SendConnectionLinkModal.scss';

const RAW = {interpolation: {escapeValue: false}};
const LOAD_ERROR = 'LOAD_ERROR';
const COPIED_FEEDBACK_MS = 2500;

export const LINK_ALIVE = 'alive';
export const LINK_USED = 'used';
export const LINK_REVOKED = 'revoked';
export const LINK_EXPIRED = 'expired';

const STATE_TEXT_KEY = {
    [LINK_ALIVE]: 'mercado_ads_link.send.expires_on',
    [LINK_USED]: 'mercado_ads_link.send.used_on',
    [LINK_REVOKED]: 'mercado_ads_link.send.revoked_on',
    [LINK_EXPIRED]: 'mercado_ads_link.send.expired_on',
};

// El backend manda el estado ya resuelto (`status`), con su reloj: manda ése. Las fechas quedan
// como respaldo para una fila sin `status`.
const STATE_BY_STATUS = {ACTIVE: LINK_ALIVE, USED: LINK_USED, EXPIRED: LINK_EXPIRED, REVOKED: LINK_REVOKED};
const DATE_FIELD_BY_STATE = {
    [LINK_ALIVE]: 'expires_at', [LINK_USED]: 'used_at', [LINK_EXPIRED]: 'expires_at', [LINK_REVOKED]: 'revoked_at',
};

/** En qué está un link de la lista y qué fecha lo describe. */
export function linkState(link, now = new Date()) {
    const fromBackend = STATE_BY_STATUS[link.status];
    if (fromBackend) return {state: fromBackend, date: link[DATE_FIELD_BY_STATE[fromBackend]]};
    if (link.used_at) return {state: LINK_USED, date: link.used_at};
    if (link.revoked_at) return {state: LINK_REVOKED, date: link.revoked_at};
    if (link.expires_at && new Date(link.expires_at) <= now) return {state: LINK_EXPIRED, date: link.expires_at};
    return {state: LINK_ALIVE, date: link.expires_at};
}

/** Los links de ese canal, el más nuevo arriba. */
export function sentLinksOf(links, channel) {
    return links
        .filter((link) => link.channel === channel)
        .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
}

function generateErrorKey(response) {
    if (response.status === 'CONNECTOR_NOT_CONFIGURED') return 'mercado_ads_link.send.error_not_configured';
    if (isNetworkError(response)) return 'mercado_ads_link.send.error_network';
    return 'mercado_ads_link.send.error_generic';
}

function isPresent(value) {
    return value !== undefined && value !== null && value !== '';
}

/**
 * «Enviar link al vendedor» (contrato §8 y §10): la agencia genera un link de un solo uso, que vence en
 * 7 días, para que el vendedor conecte su cuenta principal de Mercado Libre desde el celular sin
 * usuario de Dashbo. Lo montan las puertas de `ma-frontend` (el paso previo del alta, el error de
 * colaborador, la conexión cortada de un cliente y la ficha 2.1).
 *
 * Sin `agencyClientId`, primero se elige el cliente. El link se muestra UNA sola vez (el backend guarda
 * sólo su hash): por eso el aviso «Cópialo ahora», y por eso la lista «Links enviados» no tiene «copiar».
 */
export function SendConnectionLinkModal({show, onHide, channel, agencyClientId}) {
    const {t, i18n} = useTranslation();
    const fieldId = useId();
    const fixedClient = isPresent(agencyClientId);
    const [clients, loadClients] = useAgencyClients(show && !fixedClient);
    const [selected, setSelected] = useState(fixedClient ? String(agencyClientId) : '');
    const [generating, setGenerating] = useState(false);
    const [generateError, setGenerateError] = useState(null);
    const [fresh, setFresh] = useState(null);
    const [copied, setCopied] = useState(null);
    const [copyFailed, setCopyFailed] = useState(false);
    const [links, setLinks] = useState(null);
    const [revokingId, setRevokingId] = useState(null);
    const [revokeErrorId, setRevokeErrorId] = useState(null);
    const selectedRef = useRef(selected);
    const linksSeq = useRef(0);
    // Sube con cada apertura y cada cambio de cliente: una respuesta de «Generar» de una vista anterior
    // no puede volver a mostrar, para copiar, un link que mientras tanto se pudo haber anulado.
    const viewSeq = useRef(0);
    const copiedTimer = useRef(null);

    const choose = useCallback((value) => {
        selectedRef.current = value;
        linksSeq.current += 1;
        viewSeq.current += 1;
        setSelected(value);
        setGenerating(false);
        setFresh(null);
        setGenerateError(null);
        setCopied(null);
        setCopyFailed(false);
        setRevokeErrorId(null);
        setLinks(null);
    }, []);

    // Cada apertura arranca limpia: el link recién generado no se vuelve a mostrar (no lo tenemos).
    useEffect(() => {
        if (show) choose(fixedClient ? String(agencyClientId) : '');
    }, [show, fixedClient, agencyClientId, choose]);

    const loadLinks = useCallback((clientId) => {
        const seq = ++linksSeq.current;
        setLinks(null);
        listConnectionLinks(clientId, channel).then((response) => {
            if (seq !== linksSeq.current) return;
            setLinks(isOk(response) && Array.isArray(response.data) ? response.data : LOAD_ERROR);
        });
    }, [channel]);

    useEffect(() => {
        if (show && selected) loadLinks(selected);
    }, [show, selected, loadLinks]);

    useEffect(() => () => clearTimeout(copiedTimer.current), []);

    const generate = () => {
        if (generating || !selected) return;
        const clientId = selected;
        const seq = viewSeq.current;
        setGenerating(true);
        setGenerateError(null);
        createConnectionLink(channel, asClientId(clientId)).then((response) => {
            if (seq !== viewSeq.current) return;
            setGenerating(false);
            if (isOk(response) && response.data && response.data.url) {
                setFresh(response.data);
                loadLinks(clientId);
            } else {
                setGenerateError(generateErrorKey(response));
            }
        });
    };

    const copy = (what) => {
        const text = what === 'link'
            ? fresh.url
            : t('mercado_ads_link.send.whatsapp_message', {url: fresh.url, ...RAW});
        copyText(text).then(() => {
            setCopyFailed(false);
            setCopied(what);
            clearTimeout(copiedTimer.current);
            copiedTimer.current = setTimeout(() => setCopied(null), COPIED_FEEDBACK_MS);
        }, () => {
            setCopied(null);
            setCopyFailed(true);
        });
    };

    const revoke = (link) => {
        const clientId = selectedRef.current;
        setRevokingId(link.id);
        setRevokeErrorId(null);
        revokeConnectionLink(link.id).then((response) => {
            setRevokingId(null);
            if (clientId !== selectedRef.current) return;
            if (isOk(response)) {
                // El backend devuelve la fila en su estado final: si el vendedor lo usó mientras tanto, dice
                // USED y no REVOKED. Sin `data.status` (no debería pasar), se da por anulado.
                const updated = response.data && response.data.status
                    ? response.data
                    : {status: 'REVOKED', revoked_at: new Date().toISOString()};
                setLinks((prev) => Array.isArray(prev)
                    ? prev.map((l) => (l.id === link.id ? {...l, ...updated} : l))
                    : prev);
                // Que no quede a la vista, para copiar, un link que ya no sirve.
                setFresh((prev) => (prev && prev.id === link.id ? null : prev));
            } else {
                setRevokeErrorId(link.id);
            }
        });
    };

    const date = (value) => formatShortDate(value, i18n.language);

    const renderClientPicker = () => (fixedClient ? null : (
        <AgencyClientPicker id={fieldId} clients={clients} value={selected} onChange={choose} onRetry={loadClients}/>
    ));

    const renderGenerate = () => (
        <div className="scl-generate">
            <button type="button" className="btn -alt" onClick={generate} disabled={generating}
                    aria-busy={generating || undefined}>
                {generating ? <Spinner color="negative small"/> : t('mercado_ads_link.send.generate')}
            </button>
            {generateError && <p className="scl-error" role="alert">{t(generateError)}</p>}
        </div>
    );

    const renderFresh = () => (
        <div className="scl-fresh">
            <label htmlFor={`${fieldId}-url`} className="scl-label">{t('mercado_ads_link.send.link_label')}</label>
            <input id={`${fieldId}-url`} type="text" className="form-control scl-url" readOnly value={fresh.url}
                   onFocus={(event) => event.target.select()}/>
            <div className="scl-actions">
                <button type="button" className="btn -alt" onClick={() => copy('message')}>
                    {copied === 'message' ? t('mercado_ads_link.send.copied') : t('mercado_ads_link.send.copy_whatsapp')}
                </button>
                <button type="button" className="btn -negative" onClick={() => copy('link')}>
                    {copied === 'link' ? t('mercado_ads_link.send.copied') : t('mercado_ads_link.send.copy_link')}
                </button>
            </div>
            <span className="visually-hidden" aria-live="polite">{copied ? t('mercado_ads_link.send.copied') : ''}</span>
            {copyFailed && <p className="scl-error" role="alert">{t('mercado_ads_link.send.copy_failed')}</p>}
            <p className="scl-hint">
                {t('mercado_ads_link.send.expires_on', {date: date(fresh.expires_at), ...RAW})}
                {' · '}
                {t('mercado_ads_link.send.copy_now')}
            </p>
        </div>
    );

    const renderSent = () => {
        if (links === null) {
            return <div className="scl-inline-loader"><Spinner color="positive small"/></div>;
        }
        if (links === LOAD_ERROR) {
            return (
                <p className="scl-error" role="alert">
                    {t('mercado_ads_link.send.links_load_error')}{' '}
                    <button type="button" className="scl-text-btn" onClick={() => loadLinks(selected)}>
                        {t('mercado_ads_link.send.retry')}
                    </button>
                </p>
            );
        }
        const sent = sentLinksOf(links, channel);
        if (sent.length === 0) return null;
        return (
            <div className="scl-sent">
                <h3 className="scl-sent-title">{t('mercado_ads_link.send.active_links')}</h3>
                <ul className="scl-sent-list">
                    {sent.map((link) => {
                        const {state, date: stateDate} = linkState(link);
                        return (
                            <li key={link.id} className={`scl-sent-row -${state}`}>
                                <span className="scl-sent-text">
                                    <span>{t('mercado_ads_link.send.created_on', {date: date(link.created_at), ...RAW})}</span>
                                    <span className="scl-sent-state">
                                        {t(STATE_TEXT_KEY[state], {date: date(stateDate), ...RAW})}
                                    </span>
                                    {revokeErrorId === link.id && (
                                        <span className="scl-error" role="alert">{t('mercado_ads_link.send.revoke_failed')}</span>
                                    )}
                                </span>
                                {state === LINK_ALIVE && (
                                    <button type="button" className="scl-text-btn -danger" onClick={() => revoke(link)}
                                            disabled={revokingId === link.id}>
                                        {t('mercado_ads_link.send.revoke')}
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    };

    return (
        // Con un link a la vista, un clic en el fondo o Escape no cierran: el link se muestra una sola vez,
        // y perderlo antes de copiarlo deja uno vivo en la base y otro más cuando la agencia genera de nuevo.
        // Se cierra con «Cerrar» o la X.
        <Modal isOpen={show} toggle={onHide} className="scl-modal"
               backdrop={fresh ? 'static' : true} keyboard={!fresh}>
            <ModalHeader toggle={onHide}>{t('mercado_ads_link.send.title')}</ModalHeader>
            <ModalBody>
                <div className="modal-section">
                    <p className="scl-help">{t('mercado_ads_link.send.help')}</p>
                    {renderClientPicker()}
                    {selected && (fresh ? renderFresh() : renderGenerate())}
                    {selected && renderSent()}
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn -negative" onClick={onHide}>
                    {t('mercado_ads_link.send.close')}
                </button>
            </ModalFooter>
        </Modal>
    );
}

export default SendConnectionLinkModal;
